"use client"

import { useRouter } from "next/router"

import { Button, cn, SofiaMark } from "@coval/ui"

import { useCoviOptional } from "../contexts/CoviContext"
import type { CoviContextValue } from "../contexts/CoviContext.types"
import { isSofiaPanelAvailable } from "../utils/routes"

/**
 * The app-shell header affordance that opens Sofia — the integrated-panel
 * replacement for the old floating launcher. It is wired into the shell's
 * `renderHeaderTrailing` slot by the navigation module. It self-gates: nothing
 * renders unless a CoviProvider is present AND the panel is currently closed
 * AND the current route permits the Assistant Panel (once open, the panel's
 * own close button and the ⌘/Ctrl-I shortcut handle closing). Reads the context
 * via the non-throwing optional hook so it is safe to render in shells/tests
 * that have no provider — the signed-out app shell renders this slot with no
 * providers at all, so the provider-dependent work lives in the inner
 * component that only mounts once one is proven present.
 *
 * A page's Sofia summary is not presented here. It belongs to the conversation,
 * so it lives in the chat and nowhere else — see
 * `useSofiaPageSummaryPresenter`.
 */
export function CoviHeaderToggle({ className }: { className?: string }) {
  const copilot = useCoviOptional()
  const router = useRouter()

  if (
    !copilot ||
    copilot.isOpen ||
    !isSofiaPanelAvailable({
      pathname: router.pathname,
      asPath: router.asPath,
    })
  ) {
    return null
  }

  return <CoviHeaderToggleButton className={className} onOpen={copilot.open} />
}

function CoviHeaderToggleButton({
  className,
  onOpen,
}: {
  className?: string
  onOpen: CoviContextValue["open"]
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      // This affordance only renders while the panel is closed, so it always
      // opens — track it as a header-toggle open (not a generic shortcut).
      onClick={() => onOpen("toggle")}
      aria-label="Ask Sofia"
      title="Ask Sofia (⌘/Ctrl-I)"
      className={cn("gap-1.5", className)}
    >
      <SofiaMark size={16} className="rounded" />
      Ask Sofia
    </Button>
  )
}
